import { InboxreaderPurchase } from "@/utils/purchase/inboxreader-purchase";
import { InboxreaderPurchaseFile } from "@/utils/purchase/inboxreader-purchase-file";
import { printHumanFloat } from "@/utils/pretty-print";
import { visibleFiles } from "./inbox-list-state";
import { parseDayMonthYear } from "./dates";

// A row of the split view: one document, which is a peppol order or one
// attachment of a mail or an upload. The card and list views keep their own
// copy of these rules; this file is only used by the split view.
export interface InboxDocumentRow {
	key:string;
	purchase:InboxreaderPurchase;
	file:InboxreaderPurchaseFile;
}

export type InboxRowSource = "peppol" | "email" | "upload";

export function documentRows(purchases:InboxreaderPurchase[], archive:boolean):InboxDocumentRow[]{
	let rows:InboxDocumentRow[] = [];
	for (let purchase of purchases) {
		for (let file of visibleFiles(purchase, archive)) {
			rows.push({key: `${purchase.ID}-${file.ID}`, purchase, file});
		}
	}
	return rows;
}

export function rowSource(purchase:InboxreaderPurchase):InboxRowSource{
	if (purchase.PeppolOrder) return "peppol";
	return purchase.isUpload() ? "upload" : "email";
}

export function rowSourceIcon(purchase:InboxreaderPurchase):string | string[]{
	let source = rowSource(purchase);
	if (source == "peppol") return "share-alt";
	return source == "upload" ? "cloud-upload-alt" : ["far", "envelope"];
}

export function rowSupplier(row:InboxDocumentRow):string{
	if (row.purchase.PeppolOrder) return row.purchase.PeppolOrder.Supplier.Name;
	return row.file.getSupplierLabel() || row.purchase.From;
}

// rowReference is the line under the supplier: the number of the peppol order
// or the name of the file that came in.
export function rowReference(row:InboxDocumentRow):string{
	if (row.purchase.PeppolOrder) return row.purchase.PeppolOrder.OrderNumber;
	return row.file.getName();
}

export function rowInvoiceNumber(row:InboxDocumentRow):string{
	if (row.purchase.PeppolOrder) return row.purchase.PeppolOrder.OrderNumber;
	return row.file.getDocumentInfo()?.InvoiceNr || "";
}

export function rowAmount(row:InboxDocumentRow):string{
	if (!row.purchase.PeppolOrder) return row.file.getTotalLabel();
	return formatAmount(row.purchase.PeppolOrder.TotalIncl, row.purchase.PeppolOrder.Currency);
}

// rowAmountExcl is only known for a peppol order and for what the analysis read
// out of the document.
export function rowAmountExcl(row:InboxDocumentRow):string{
	if (row.purchase.PeppolOrder) {
		return formatAmount(row.purchase.PeppolOrder.TotalExcl, row.purchase.PeppolOrder.Currency);
	}
	let net = parseFloat(row.file.getDocumentInfo()?.TotalNet || "");
	if (isNaN(net)) return "";
	return formatAmount(net, row.file.getDocumentInfo()?.Currency || "");
}

// rowVatPercentage is left empty as soon as a document holds more than one vat
// rate: one number would be a lie then.
export function rowVatPercentage(row:InboxDocumentRow):string{
	let order = row.purchase.PeppolOrder;
	if (!order || order.VatGroups.length != 1) return "";
	return `${printHumanFloat(order.VatGroups[0].VATPercentage, 0)}%`;
}

export function rowInvoiceDate(row:InboxDocumentRow):Date | null{
	if (row.purchase.PeppolOrder) return validDate(row.purchase.PeppolOrder.OrderDate);
	return parseDayMonthYear(row.file.getInvoiceDate());
}

export function rowDueDate(row:InboxDocumentRow):Date | null{
	if (row.purchase.PeppolOrder) return validDate(row.purchase.PeppolOrder.ExpiryDate);
	return parseDayMonthYear(row.file.getDueDate());
}

export function rowVatNumber(row:InboxDocumentRow):string{
	if (row.purchase.PeppolOrder) return row.purchase.PeppolOrder.Supplier.VATNumber;
	return row.file.getDocumentInfo()?.SupplierVAT || "";
}

function formatAmount(value:number, currency:string):string{
	let amount = printHumanFloat(value);
	if (!currency || currency == "EUR") return `€ ${amount}`;
	return `${currency} ${amount}`;
}

// A date that the backend does not know comes in as a zero date.
function validDate(date:Date | null):Date | null{
	if (!date || date.getFullYear() <= 1) return null;
	return date;
}
